import React from "react";

import Button from "../buttons/Button";
import {
    OrganisationUnit,
    OrganisationUnitType,
} from "../../types/core/OrganisationUnit";
import DropDown, { DropDownOption } from "../inputform/DropDown";
import Textbox from "../inputform/Textbox";
import Utilities from "../../foundation/Utility";

import "./OrgUnit.scss";

interface OrgUnitProps {
    parent?: OrganisationUnit;
    value: OrganisationUnit;
    className?: string;

    onTypeChange?: (
        orgUnit: OrganisationUnit,
        type: OrganisationUnitType,
    ) => void;
    onNameChange?: (orgUnit: OrganisationUnit, name: string) => void;
    onAdd?: (orgUnit: OrganisationUnit) => void;
    onRemove?: (orgUnit: OrganisationUnit) => void;
    onHeightChange?: () => void;
}

interface OrgUnitState {
    ref: React.RefObject<HTMLDivElement>;
    open: boolean;
    types: DropDownOption[];
}

class OrgUnit extends React.Component<OrgUnitProps, OrgUnitState> {
    state: OrgUnitState = {
        ref: React.createRef<HTMLDivElement>(),
        open: false,
        types: [
            { text: "School", value: "School", className: "school" },
            { text: "Campus", value: "Campus", className: "campus" },
            { text: "Year Level", value: "YearLevel", className: "year-level" },
            { text: "Faculty", value: "Faculty", className: "faculty" },
            { text: "Department", value: "Department", className: "department" },
        ],
    };

    toggleIcon() {
        if ((this.props.value?.children?.length ?? 0) > 0) {
            if (this.state.open) {
                return "arrow_drop_down";
            } else {
                return "arrow_right";
            }
        } else {
            return "";
        }
    }

    itemIcon() {
        switch (this.props.value?.type) {
            case OrganisationUnitType.School:
                return "location_city";
            case OrganisationUnitType.Campus:
                return "domain";
            case OrganisationUnitType.YearLevel:
                return "signal_cellular_alt";
            case OrganisationUnitType.Faculty:
                return "history_edu";
            case OrganisationUnitType.Department:
                return "group_work";
        }
    }

    itemLocked() {
        switch (this.props.value?.type) {
            case OrganisationUnitType.School:
            case OrganisationUnitType.Campus:
                return true;
            default:
                return false;
        }
    }

    addChild() {
        this.props.onAdd?.(this.props.value);
        this.setState({ open: true }, () => {
            this.calculateChildren();
        });
    }

    toggleChildren() {
        this.setState({ open: !this.state.open }, () => {
            this.calculateChildren();
        });
    }

    async calculateChildren() {
        const children = this.state.ref.current?.querySelector(
            ".children",
        ) as HTMLElement;
        if (!this.state.open) children.style.maxHeight = "0px";
        else {
            const container = children.querySelector(
                ".children-conatiner",
            ) as HTMLElement;
            children.style.maxHeight = `${container.offsetHeight}px`;
        }
        await Utilities.sleep(100);
        this.props.onHeightChange?.();
    }

    render(): React.ReactNode {
        let classNames = "organisation-unit";

        if (this.props.className) classNames += ` ${this.props.className}`;
        if (this.itemLocked()) classNames += " locked";
        if (this.props.value?.children?.length) classNames += " has-children";

        let childClasses = "children";

        if (this.state.open) childClasses += " open";

        return (
            <div
                ref={this.state.ref}
                className={classNames}>
                {/* Main details container for the unit */}
                <div className="details">
                    {/* Toggle button for expanding/collapsing children */}
                    <Button
                        icon={this.toggleIcon()}
                        className="toggle-children"
                        onClick={this.toggleChildren.bind(this)}
                    />
                    {/* Type selection button with icon */}
                    <DropDown
                        button={true}
                        icon={this.itemIcon()}
                        className="type-icon"
                        value={this.props.value?.type}
                        disabled={this.itemLocked()}
                        onChange={value =>
                            this.props.onTypeChange?.(this.props.value, value)
                        }
                        options={this.state.types}
                    />
                    {/* Name input field */}
                    <div className="name">
                        <Textbox
                            className="full"
                            readonly={this.itemLocked()}
                            value={this.props.value?.name}
                            onChange={value =>
                                this.props.onNameChange?.(
                                    this.props.value,
                                    value,
                                )
                            }
                        />
                    </div>
                    {/* Remove button */}
                    <Button
                        icon="close"
                        className="remove"
                        tooltip="Remove this unit"
                        onClick={() => this.props.onRemove?.(this.props.value)}
                    />
                    {/* Add child button */}
                    <Button
                        icon="add"
                        className="add-child"
                        tooltip="Add a new child unit"
                        onClick={this.addChild.bind(this)}
                    />
                </div>
                {/* Children container with animation support */}
                {!!this.props.value.children?.length && (
                    <div className={childClasses}>
                        <div className="children-conatiner">
                            {this.props.value.children.map(child => (
                                <OrgUnit
                                    key={child.id}
                                    parent={this.props.value}
                                    value={child}
                                    onTypeChange={this.props.onTypeChange}
                                    onNameChange={this.props.onNameChange}
                                    onAdd={this.props.onAdd}
                                    onRemove={this.props.onRemove}
                                    onHeightChange={this.calculateChildren.bind(
                                        this,
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default OrgUnit;
